import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import type {DragEndEvent, DragStartEvent} from "@dnd-kit/core";
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {Column, RowData, Table as TanStackTable} from "@tanstack/react-table";
import type {ReactNode} from "react";
import {useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {SERVER_TABLE_EXPANSION_COLUMN_ID} from "../ServerTableExpansionColumn";
import {SERVER_TABLE_SELECTION_COLUMN_ID} from "../ServerTableSelectionColumn";
import {COLUMN_DRAG_ACTIVATION_DISTANCE, SERVER_TABLE_PANEL_WIDTH} from "./constants";

const SERVER_TABLE_UTILITY_COLUMN_IDS = new Set([
    SERVER_TABLE_SELECTION_COLUMN_ID,
    SERVER_TABLE_EXPANSION_COLUMN_ID
]);

interface ColumnConfiguratorPanelProps<TData extends RowData> {
    table: TanStackTable<TData>
    onClose?: () => void
}

// Compare IDs in order because position is part of the state.
const arraysMatch = (left: string[] = [], right: string[] = []) => (
    left.length === right.length && left.every((value, index) => value === right[index])
);

// Keep hidden columns in the complete order so restoring them is deterministic.
const mergeColumnOrder = <TData extends RowData>(
    visibleColumns: Array<Column<TData, unknown>>,
    hiddenColumns: Array<Column<TData, unknown>>
) => [
    ...visibleColumns.map(column => column.id),
    ...hiddenColumns.map(column => column.id)
];

const ColumnRowContent = ({
                              label,
                              checkbox,
                              actions,
                              labelCursor = 'inherit'
                          }: {
    label: string
    checkbox: ReactNode
    actions: ReactNode
    labelCursor?: 'default' | 'inherit' | 'pointer'
}) => (
    <>
        <Box component="label" sx={{display: 'flex', alignItems: 'center', minWidth: 0, cursor: labelCursor}}>
            {checkbox}
            <Typography variant="body1" noWrap sx={{minWidth: 0, flex: 1}}>
                {label}
            </Typography>
        </Box>
        {actions}
    </>
);

const SortableColumnRow = <TData extends RowData>({
                                                      column,
                                                      label
                                                  }: {
    column: Column<TData, unknown>
    label: string
}) => {
    const {t} = useTranslation();
    const isPinned = column.getIsPinned() === 'left';
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: column.id});

    return (
        <Box
            ref={setNodeRef}
            style={{transform: CSS.Transform.toString(transform), transition}}
            sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.5,
                border: '1px solid',
                borderColor: isPinned ? 'primary.main' : 'transparent',
                borderRadius: 1,
                opacity: isDragging ? 0.35 : 1,
                '&:hover': {backgroundColor: 'action.hover'}
            }}
        >
            <ColumnRowContent
                label={label}
                labelCursor="pointer"
                checkbox={(
                    <Checkbox
                        size="small"
                        checked
                        onChange={() => {
                            column.pin(false);
                            column.toggleVisibility(false);
                        }}
                        sx={{p: 0.5, mr: 0.5}}
                    />
                )}
                actions={(
                    <Stack direction="row" alignItems="center" gap={1}>
                        {column.getCanPin() && (
                            <Tooltip title={isPinned ? t('Unpin {{label}}', {label}) : t('Pin {{label}}', {label})}
                                     arrow>
                                <Button
                                    type="button"
                                    size="small"
                                    variant={isPinned ? 'contained' : 'text'}
                                    aria-label={isPinned ? t('Unpin {{label}}', {label}) : t('Pin {{label}}', {label})}
                                    onClick={() => column.pin(isPinned ? false : 'left')}
                                    sx={{
                                        minWidth: 24,
                                        width: 24,
                                        maxWidth: 24,
                                        height: 24,
                                        minHeight: 24,
                                        maxHeight: 24,
                                        p: 0,
                                        borderRadius: 1
                                    }}
                                >
                                    {isPinned
                                        ? <PushPinIcon sx={{fontSize: 15}}/>
                                        : <PushPinOutlinedIcon sx={{fontSize: 15}}/>}
                                </Button>
                            </Tooltip>
                        )}

                        <Tooltip title={t('Move column')} arrow>
                            <Box
                                {...attributes}
                                {...listeners}
                                role="button"
                                tabIndex={0}
                                aria-label={t('Move {{label}}', {label})}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    color: 'text.disabled',
                                    cursor: 'grab',
                                    touchAction: 'none',
                                    '&:active': {cursor: 'grabbing'}
                                }}
                            >
                                <DragIndicatorIcon fontSize="small"/>
                            </Box>
                        </Tooltip>
                    </Stack>
                )}
            />
        </Box>
    );
};

const AvailableColumnRow = <TData extends RowData>({
                                                       column,
                                                       label,
                                                       onRestore
                                                   }: {
    column: Column<TData, unknown>
    label: string
    onRestore: (column: Column<TData, unknown>) => void
}) => (
    <Box
        component="label"
        sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            px: 0.75,
            py: 0.5,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {backgroundColor: 'action.hover'}
        }}
    >
        <Checkbox
            size="small"
            checked={false}
            onChange={() => onRestore(column)}
            sx={{p: 0.5, mr: 0.5}}
        />
        <Typography variant="body1" noWrap sx={{minWidth: 0}}>
            {label}
        </Typography>
    </Box>
);

const ColumnConfiguratorPanel = <TData extends RowData>({
                                                            table,
                                                            onClose
                                                        }: ColumnConfiguratorPanelProps<TData>) => {
    const {t} = useTranslation();
    // The table owns preferences; local state only tracks the active drag.
    const [activeDragId, setActiveDragId] = useState<string | null>(null);
    const allColumns = table.getAllLeafColumns()
        .filter(column => !SERVER_TABLE_UTILITY_COLUMN_IDS.has(column.id));
    // Base order stays stable when pinning moves a column into another rendered region.
    const visibleColumns = allColumns.filter(column => column.getIsVisible());
    const labelFor = (column: Column<TData, unknown>) => (
        typeof column.columnDef.header === 'string'
            ? column.columnDef.header
            : column.columnDef.meta?.headerLabel ?? column.id
    );
    // Alphabetize hidden columns for discovery rather than table order.
    const hiddenColumns = allColumns
        .filter(column => !column.getIsVisible())
        .sort((left, right) => labelFor(left).localeCompare(labelFor(right)));
    const allVisible = visibleColumns.length === allColumns.length;
    const defaultVisibility = table.initialState.columnVisibility ?? {};
    const defaultPinning = table.initialState.columnPinning ?? {};
    const currentPinning = table.getState().columnPinning;
    const currentOrder = table.getState().columnOrder;
    const visibilityIsDefault = allColumns.every(column => (
        column.getIsVisible() === (defaultVisibility[column.id] ?? true)
    ));
    const pinningIsDefault = arraysMatch(currentPinning.left, defaultPinning.left)
        && arraysMatch(currentPinning.right, defaultPinning.right);
    const orderIsDefault = arraysMatch(currentOrder, table.initialState.columnOrder);
    const sizingIsDefault = Object.keys(table.getState().columnSizing).length === 0;
    // Visibility, pinning, order, and sizing together define default preferences.
    const preferencesAreDefault = visibilityIsDefault && pinningIsDefault && orderIsDefault && sizingIsDefault;
    // Require deliberate pointer movement while keeping keyboard sorting available.
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: COLUMN_DRAG_ACTIVATION_DISTANCE}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );
    const draggedColumn = activeDragId
        ? visibleColumns.find(column => column.id === activeDragId)
        : undefined;

    const toggleAllColumns = () => {
        if (allVisible) {
            table.setColumnVisibility(Object.fromEntries(
                allColumns.map(column => [column.id, false])
            ));
            table.setColumnPinning({left: [], right: []});
            return;
        }

        table.toggleAllColumnsVisible(true);
    };

    const resetColumns = () => {
        // Reset related preference slices together to restore a coherent table state.
        table.resetColumnVisibility();
        table.resetColumnPinning();
        table.resetColumnOrder();
        table.resetColumnSizing();
    };

    const restoreColumn = (column: Column<TData, unknown>) => {
        column.toggleVisibility(true);
        // Append restored columns to the visible section of the complete order.
        table.setColumnOrder(mergeColumnOrder(
            [...visibleColumns, column],
            hiddenColumns.filter(item => item.id !== column.id)
        ));
    };

    const onDragStart = ({active}: DragStartEvent) => {
        setActiveDragId(String(active.id));
    };

    const onDragEnd = ({active, over}: DragEndEvent) => {
        setActiveDragId(null);

        if (!over || active.id === over.id) {
            return;
        }

        const fromIndex = visibleColumns.findIndex(column => column.id === active.id);
        const toIndex = visibleColumns.findIndex(column => column.id === over.id);

        if (fromIndex < 0 || toIndex < 0) {
            return;
        }

        const reorderedColumns = arrayMove(visibleColumns, fromIndex, toIndex);
        // Drag state contains only visible columns, so rebuild the complete order before committing it.
        table.setColumnOrder(mergeColumnOrder(reorderedColumns, hiddenColumns));
        // General and pinned-region order must remain synchronized after every drag.
        table.setColumnPinning({
            left: reorderedColumns
                .filter(column => column.getIsPinned() === 'left')
                .map(column => column.id),
            right: reorderedColumns
                .filter(column => column.getIsPinned() === 'right')
                .map(column => column.id)
        });
    };

    return (
        <Stack
            sx={{
                width: SERVER_TABLE_PANEL_WIDTH,
                minWidth: 0,
                minHeight: 0,
                flex: 1,
                maxHeight: 'calc(100dvh - 32px)',
                overflow: 'hidden'
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2, pb: 1, flexShrink: 0}}>
                <Typography variant="body1" fontWeight={500}>
                    {t('Columns')}
                </Typography>

                {onClose && (
                    <IconButton size="small" aria-label={t('Close')} onClick={onClose} sx={{ml: 'auto'}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                pb: 1,
                flexShrink: 0
            }}>
                <Button
                    size="small"
                    color="info"
                    disabled={!allColumns.length}
                    onClick={toggleAllColumns}
                >
                    {allVisible ? t('Deselect all') : t('Select all')}
                </Button>

                {!preferencesAreDefault && (
                    <Button size="small" color="inherit" onClick={resetColumns}>
                        {t('Reset')}
                    </Button>
                )}
            </Box>

            <Stack
                gap={0.25}
                sx={{
                    px: 1.25,
                    pb: 1.5,
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    scrollbarGutter: 'stable',
                    overscrollBehavior: 'contain'
                }}
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragStart={onDragStart}
                    onDragCancel={() => setActiveDragId(null)}
                    onDragEnd={onDragEnd}
                >
                    <SortableContext
                        items={visibleColumns.map(column => column.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {visibleColumns.map(column => (
                            <SortableColumnRow
                                key={column.id}
                                column={column}
                                label={labelFor(column)}
                            />
                        ))}
                    </SortableContext>

                    {/*
                        The drag preview lives outside the scroll container so
                        overflow cannot clip it.
                    */}
                    {createPortal(
                        <DragOverlay zIndex={1500} dropAnimation={null}>
                            {draggedColumn && (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        width: '100%',
                                        maxWidth: 'calc(100vw - 32px)',
                                        boxSizing: 'border-box',
                                        px: 0.75,
                                        py: 0.5,
                                        border: '1px solid',
                                        borderColor: draggedColumn.getIsPinned() === 'left'
                                            ? 'primary.main'
                                            : 'transparent',
                                        borderRadius: 1,
                                        backgroundColor: 'background.paper',
                                        boxShadow: '0 8px 22px rgba(15, 23, 42, 0.18)',
                                        cursor: 'grabbing'
                                    }}
                                >
                                    <ColumnRowContent
                                        label={labelFor(draggedColumn)}
                                        checkbox={<Checkbox size="small" checked readOnly sx={{p: 0.5, mr: 0.5}}/>}
                                        actions={(
                                            <Stack direction="row" alignItems="center" gap={1}>
                                                {draggedColumn.getCanPin() && (
                                                    <Button
                                                        type="button"
                                                        size="small"
                                                        variant={draggedColumn.getIsPinned() === 'left' ? 'contained' : 'text'}
                                                        tabIndex={-1}
                                                        sx={{
                                                            minWidth: 24,
                                                            width: 24,
                                                            maxWidth: 24,
                                                            height: 24,
                                                            minHeight: 24,
                                                            maxHeight: 24,
                                                            p: 0,
                                                            borderRadius: 1,
                                                            pointerEvents: 'none'
                                                        }}
                                                    >
                                                        {draggedColumn.getIsPinned() === 'left'
                                                            ? <PushPinIcon sx={{fontSize: 15}}/>
                                                            : <PushPinOutlinedIcon sx={{fontSize: 15}}/>}
                                                    </Button>
                                                )}
                                                <Box sx={{
                                                    display: 'inline-flex',
                                                    width: 24,
                                                    height: 24,
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'text.disabled'
                                                }}>
                                                    <DragIndicatorIcon fontSize="small"/>
                                                </Box>
                                            </Stack>
                                        )}
                                    />
                                </Box>
                            )}
                        </DragOverlay>,
                        document.body
                    )}
                </DndContext>

                {!!hiddenColumns.length && (
                    <Typography variant="overline" sx={{px: 0.75, pt: 1, color: 'text.secondary', lineHeight: 2}}>
                        {t('Available')}
                    </Typography>
                )}

                {hiddenColumns.map(column => (
                    <AvailableColumnRow
                        key={column.id}
                        column={column}
                        label={labelFor(column)}
                        onRestore={restoreColumn}
                    />
                ))}

            </Stack>

        </Stack>
    );
};

export default ColumnConfiguratorPanel;
